import React from 'react';
import EmptyState from '../shared/EmptyState';
import ModalPortal from '../../../../components/ModalPortal';

/**
 * CreateOrganizationModal - Modal for creating a new organization
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Called when the modal should close
 * @param {Object} props.newOrganization - The new organization data { id, name, rootAdminUserId }
 * @param {Function} props.setNewOrganization - Called when organization data changes
 * @param {string} props.organizationError - Error message to display
 * @param {Function} props.setOrganizationError - Called when error changes
 * @param {boolean} props.organizationSaving - Whether the organization is being saved
 * @param {Function} props.onSubmit - Called when the form is submitted
 * @param {Array} props.users - Array of user objects for autocomplete
 * @param {string} props.ownerSearch - Search term for owner autocomplete
 * @param {Function} props.setOwnerSearch - Called when owner search changes
 * @param {boolean} props.isOwnerDropdownOpen - Whether owner dropdown is open
 * @param {Function} props.setIsOwnerDropdownOpen - Called when owner dropdown toggle
 * @param {Array} props.filteredOwnerOptions - Filtered users for owner autocomplete
 * @returns {React.ReactElement} Create organization modal component
 */
const CreateOrganizationModal = ({
  isOpen,
  onClose,
  newOrganization,
  setNewOrganization,
  organizationError,
  setOrganizationError,
  organizationSaving,
  onSubmit,
  users,
  ownerSearch,
  setOwnerSearch,
  isOwnerDropdownOpen,
  setIsOwnerDropdownOpen,
  filteredOwnerOptions
}) => {
  const overlayMouseDown = React.useRef(false);

  if (!isOpen) return null;

  return (
    <ModalPortal>
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) overlayMouseDown.current = true;
      }}
      onMouseUp={(e) => {
        if (e.target === e.currentTarget && overlayMouseDown.current) {
          onClose();
        }
        overlayMouseDown.current = false;
      }}
    >
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ minWidth: '450px' }}>
        <h2 style={{ color: 'var(--text-primary)', margin: '0 0 1rem 0', fontSize: '1.25rem' }}>Create Organization</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
          Register a new corporate organization node in the system database.
        </p>
        
        {organizationError && (
          <div style={{ color: 'var(--error)', fontSize: '0.85rem', padding: '0.75rem', background: 'rgba(220, 38, 38, 0.08)', borderRadius: 'var(--radius)', border: '1px solid rgba(220, 38, 38, 0.2)', marginBottom: '1rem' }}>
            <i className="fa-solid fa-circle-exclamation" style={{ marginRight: '0.5rem' }}></i>
            {organizationError}
          </div>
        )}

        <form onSubmit={onSubmit} className="importer-form">
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Unique Organization ID</label>
            <input 
                className="input-field" 
                value={newOrganization.id}
                onChange={e => setNewOrganization({...newOrganization, id: e.target.value})}
                required
                placeholder="e.g. acme_corp"
                autoFocus
            />
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
              Spaces and dashes will be converted to underscores. Must be unique.
            </span>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Display Name</label>
            <input 
                className="input-field" 
                value={newOrganization.name}
                onChange={e => setNewOrganization({...newOrganization, name: e.target.value})}
                required
                placeholder="e.g. Acme Corporation"
            />
          </div>
          <div style={{ position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Root Admin Owner</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <i className="fa-solid fa-user-shield" style={{ position: 'absolute', left: '1rem', color: 'var(--text-secondary)' }}></i>
              <input 
                  type="text"
                  className="input-field"
                  value={ownerSearch}
                  onChange={e => {
                    setOwnerSearch(e.target.value);
                    setIsOwnerDropdownOpen(true);
                    if (newOrganization.rootAdminUserId) {
                      setNewOrganization({ ...newOrganization, rootAdminUserId: '' });
                    }
                  }}
                  onFocus={() => setIsOwnerDropdownOpen(true)}
                  placeholder="Type to search and select root owner..."
                  style={{ paddingLeft: '2.5rem', paddingRight: '2.5rem', margin: 0, width: '100%', boxSizing: 'border-box' }}
              />
              {newOrganization.rootAdminUserId && (
                <button 
                    type="button"
                    onClick={() => {
                      setNewOrganization({ ...newOrganization, rootAdminUserId: '' });
                      setOwnerSearch('');
                    }}
                    style={{ position: 'absolute', right: '1rem', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)' }}
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              )}
            </div>

            {/* Owner Autocomplete Dropdown */}
            {isOwnerDropdownOpen && (
              <>
                <div 
                    onClick={() => setIsOwnerDropdownOpen(false)} 
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 101, background: 'transparent' }} 
                />
                <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    zIndex: 102,
                    maxHeight: '180px',
                    overflowY: 'auto',
                    padding: '4px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px'
                }}>
                  {filteredOwnerOptions.length === 0 ? (
                    <EmptyState message="No matching users found." variant="inline" />
                  ) : (
                    filteredOwnerOptions.map(u => (
                      <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            setNewOrganization({ ...newOrganization, rootAdminUserId: u.id });
                            setOwnerSearch(u.fullName ? `${u.fullName} (${u.email})` : u.email);
                            setIsOwnerDropdownOpen(false);
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            borderRadius: 'calc(var(--radius) - 4px)',
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                            padding: '0.5rem 0.75rem',
                            display: 'flex',
                            flexDirection: 'column',
                            textAlign: 'left',
                            width: '100%',
                            transition: 'var(--transition)'
                          }}
                          className="table-row-hover"
                      >
                        <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{u.fullName || 'No Name'}</span>
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{u.email}</span>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
          <div className="modal-actions" style={{ marginTop: '2rem' }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={organizationSaving}>Cancel</button>
            <button type="submit" className="btn-primary" style={{ background: 'var(--text-primary)', color: 'var(--bg-primary)' }} disabled={organizationSaving}>
              {organizationSaving ? 'Saving...' : 'Create Organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </ModalPortal>
  );
};

export default CreateOrganizationModal;
